import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { t } from '@aquilixshop/i18n';
import { createPortal } from 'react-dom';
import { useProductState } from '../_shared/productContext.js';
import { mediaForVariant, selectedImageGroup } from '../_shared/productImages.js';
import { VariantImageWarmer } from '../_shared/imageWarm.js';
import { num, str, schemeColor } from '../_shared/blockRender.js';
import { SCHEME_COLOR_OPTIONS } from '../_shared/blockFields.js';
import { PlaceholderMedia } from '../../primitives/PlaceholderMedia.js';
import { ResilientImg } from '../_shared/ResilientImg.js';
import { productImageAlt } from '../_shared/imageAlt.js';
import { fetchPriorityAttr } from '../../sections/_shared/imgPriority.js';
import { clipSources, videoSourceTags } from '../../sections/_shared/videoSources.js';
import { videoContainerAttrs } from '../../sections/_shared/videoControls.js';
import { initVideos } from '../../sections/_shared/videoInit.js';
import { assetVariantUrl, assetVariantSrcSet, assetAvifSrcSet, galleryPaintedSizes, THUMBNAIL_VARIANT_WIDTH, } from '../_shared/assetVariant.js';
export const mediaGalleryBlockSchema = {
    type: 'media-gallery',
    name: 'Media gallery',
    max: 1,
    fields: [
        // ─── EXISTING ids (PRESERVED verbatim — power the validated above-the-fold preset + tests) ───
        // NOTE — Shrine-id mapping kept under OUR ids: thumbnailPosition≈desktop_thumbnails_position,
        // mediaFit≈media_fit, aspectRatio≈thumbnails_ratio, zoom≈image_zoom_desktop(hover), showThumbnails
        // (boolean; Shrine derives it from gallery_layout). `right` added to thumbnailPosition for parity.
        {
            id: 'thumbnailPosition',
            type: 'select',
            label: 'Thumbnail position',
            group: 'Desktop media',
            default: 'bottom',
            options: [
                { value: 'bottom', label: 'Below image' },
                { value: 'left', label: 'Left of image' },
                { value: 'right', label: 'Right of image' },
            ],
        },
        {
            id: 'mediaFit',
            // Shrine `media_fit` defaults to "contain" — the main image shows un-cropped inside its
            // aspect-ratio box (the elegant default). "cover" stays available for sellers who want a crop.
            type: 'select',
            label: 'Image fit',
            group: 'Desktop media',
            default: 'contain',
            options: [
                { value: 'contain', label: 'Contain (whole image)' },
                { value: 'cover', label: 'Cover (crop to fill)' },
            ],
        },
        {
            id: 'aspectRatio',
            type: 'select',
            label: 'Image ratio',
            group: 'Desktop media',
            default: 'square',
            options: [
                { value: 'natural', label: 'Natural' },
                { value: 'square', label: 'Square (1:1)' },
                { value: 'portrait', label: 'Portrait (4:5)' },
                { value: 'landscape', label: 'Landscape (3:2)' },
            ],
        },
        {
            id: 'showThumbnails',
            type: 'checkbox',
            label: 'Show thumbnails (desktop)',
            group: 'Desktop media',
            default: true,
        },
        { id: 'zoom', type: 'checkbox', label: 'Hover zoom', group: 'Media', default: true },
        // ─── ADDED: General (Shrine top/general group) ───
        {
            id: 'colorScheme',
            type: 'select',
            label: 'Color scheme',
            group: 'General',
            default: 'background-1',
            options: SCHEME_COLOR_OPTIONS,
        },
        // ─── ADDED: Media (Shrine "Media" header group) ───
        {
            id: 'imageZoomDesktop',
            type: 'select',
            label: 'Desktop image zoom',
            info: 'Lightbox opens the image full-screen on click.',
            group: 'Media',
            default: 'combined',
            options: [
                { value: 'none', label: 'None' },
                { value: 'hover', label: 'Hover' },
                { value: 'lightbox', label: 'Lightbox' },
                { value: 'combined', label: 'Hover + lightbox' },
            ],
        },
        {
            id: 'hoverZoomStyle',
            type: 'select',
            label: 'Hover zoom style',
            info: 'Inline magnifies in place; pane shows the zoom beside the image.',
            group: 'Media',
            default: 'inline',
            options: [
                { value: 'inline', label: 'Inline' },
                { value: 'pane', label: 'Pane' },
            ],
            visibleWhen: { field: 'imageZoomDesktop', equals: ['hover', 'combined'] },
        },
        {
            id: 'imageZoomMobile',
            type: 'select',
            label: 'Mobile image zoom',
            group: 'Media',
            default: 'lightbox',
            options: [
                { value: 'none', label: 'None' },
                { value: 'lightbox', label: 'Lightbox' },
            ],
        },
        {
            id: 'thumbnailsRatio',
            type: 'select',
            label: 'Thumbnails ratio',
            group: 'Media',
            default: 'square',
            options: [
                { value: 'square', label: 'Square (1:1)' },
                { value: 'original', label: 'Original' },
            ],
        },
        {
            id: 'arrowsColorScheme',
            type: 'select',
            label: 'Arrows color scheme',
            group: 'Media',
            default: 'background-1',
            options: SCHEME_COLOR_OPTIONS,
        },
        {
            id: 'transparentArrows',
            type: 'checkbox',
            label: 'Transparent arrows',
            group: 'Media',
            // Default ON: bare chevrons with no circle behind them (Shrine look). Turn OFF + pick an arrows
            // colour scheme to get a filled pill instead.
            default: true,
        },
        {
            id: 'dotsColorScheme',
            type: 'select',
            label: 'Dots color scheme',
            group: 'Media',
            default: 'text',
            options: SCHEME_COLOR_OPTIONS,
        },
        {
            // ADDED (generic, back-compat default OFF): a small rounded "current/total" chip (e.g. "1/5")
            // overlaid top-right, wired to the gallery's existing slide state (desktop active image + mobile
            // carousel slide). Renders only with 2+ images (a "1/1" counter is meaningless).
            id: 'showCounter',
            type: 'checkbox',
            label: 'Show slide counter',
            group: 'Media',
            info: 'Small "1/5" chip over the image (top right). Shown only with 2+ images.',
            default: false,
        },
        // ─── ADDED: Desktop media (Shrine "Desktop media" header group) ───
        // NOTE — no block-level `mediaSize` here: the media column width is owned by the enclosing section's
        // own media-size control, so a duplicate size control on the block would do nothing. Removed to avoid
        // a dead inspector field.
        {
            id: 'galleryLayout',
            type: 'select',
            label: 'Gallery layout',
            group: 'Desktop media',
            default: 'thumbnail_slider',
            options: [
                { value: 'stacked', label: 'Stacked' },
                { value: 'columns', label: 'Columns' },
                { value: 'thumbnail', label: 'Thumbnail' },
                { value: 'thumbnail_slider', label: 'Thumbnail slider' },
            ],
        },
        {
            id: 'desktopThumbnailsCount',
            type: 'range',
            label: 'Thumbnails visible',
            group: 'Desktop media',
            min: 4,
            max: 12,
            step: 1,
            default: 5,
        },
        {
            id: 'desktopVerticalThumbnailsWidth',
            type: 'range',
            label: 'Vertical thumbnails width',
            group: 'Desktop media',
            min: 10,
            max: 30,
            step: 2,
            default: 16,
            unit: '%',
            visibleWhen: { field: 'thumbnailPosition', equals: ['left', 'right'] },
        },
        {
            id: 'desktopVerticalThumbnailsHideArrows',
            type: 'checkbox',
            label: 'Hide vertical thumbnail arrows',
            group: 'Desktop media',
            default: false,
            visibleWhen: { field: 'thumbnailPosition', equals: ['left', 'right'] },
        },
        {
            id: 'constrainToViewport',
            type: 'checkbox',
            label: 'Constrain to viewport height',
            group: 'Desktop media',
            default: true,
        },
        {
            id: 'desktopArrowsPosition',
            type: 'select',
            label: 'Desktop arrows position',
            group: 'Desktop media',
            default: 'hidden',
            options: [
                { value: 'hidden', label: 'Hidden' },
                { value: 'sides', label: 'Sides' },
            ],
        },
        // ─── ADDED: Mobile media (Shrine "Mobile media" header group) ───
        {
            id: 'mobileMediaCornerRadius',
            type: 'range',
            label: 'Corner radius',
            group: 'Mobile media',
            min: 0,
            max: 40,
            step: 2,
            default: 0,
            unit: 'px',
        },
        {
            id: 'mobileSpacingPixels',
            type: 'range',
            label: 'Slide spacing',
            group: 'Mobile media',
            min: 0,
            max: 40,
            step: 2,
            default: 0,
            unit: 'px',
        },
        {
            id: 'mobileArrowsPosition',
            type: 'select',
            label: 'Mobile arrows position',
            group: 'Mobile media',
            // Default hidden — the mobile gallery navigates via swipe + the thumbnail strip, so no overlay arrows
            // (and therefore no white-circle controls). Sellers can turn them on; they render as bare chevrons.
            default: 'hidden',
            options: [
                { value: 'hidden', label: 'Hidden' },
                { value: 'pagination', label: 'With pagination' },
                { value: 'sides', label: 'Sides' },
            ],
        },
        {
            id: 'mobilePagination',
            type: 'select',
            label: 'Mobile pagination',
            group: 'Mobile media',
            default: 'dots_overlay',
            options: [
                { value: 'hidden', label: 'Hidden' },
                { value: 'numeric', label: 'Numeric' },
                { value: 'dots_under', label: 'Dots under' },
                { value: 'dots_overlay', label: 'Dots overlay' },
            ],
        },
        {
            id: 'mobileThumbnailsPosition',
            type: 'select',
            label: 'Mobile thumbnails position',
            group: 'Mobile media',
            // Default 'bottom' — show the thumbnail strip under the mobile carousel so every product image is
            // visible on phones (the founder's chosen mobile gallery). 'hidden' = carousel + dots only.
            default: 'bottom',
            options: [
                { value: 'hidden', label: 'Hidden' },
                { value: 'left', label: 'Left' },
                { value: 'bottom', label: 'Bottom' },
                { value: 'right', label: 'Right' },
            ],
        },
        {
            id: 'mobileThumbnailsCount',
            type: 'range',
            label: 'Mobile thumbnails visible',
            group: 'Mobile media',
            min: 3,
            max: 10,
            step: 1,
            default: 5,
        },
        // ─── ADDED: Trust badge (Shrine "Trust badge" header group) ───
        {
            id: 'trustBadge',
            type: 'image',
            label: 'Trust badge',
            group: 'Trust badge',
        },
        {
            id: 'trustBadgePosition',
            type: 'select',
            label: 'Badge position',
            group: 'Trust badge',
            default: 'top-right',
            options: [
                { value: 'top-left', label: 'Top left' },
                { value: 'top-right', label: 'Top right' },
                { value: 'bottom-left', label: 'Bottom left' },
                { value: 'bottom-right', label: 'Bottom right' },
            ],
        },
        {
            id: 'badgeDisplayOn',
            type: 'select',
            label: 'Show badge on',
            group: 'Trust badge',
            default: 'all',
            options: [
                { value: 'all', label: 'All images' },
                { value: 'first', label: 'First image only' },
            ],
        },
        {
            id: 'badgeSizePercentage',
            type: 'range',
            label: 'Badge size',
            group: 'Trust badge',
            min: 6,
            max: 50,
            step: 2,
            default: 20,
            unit: '%',
        },
        // ─── ADDED: Overlay badge (generic, back-compat default EMPTY) — a SECOND, persistent badge
        // overlay (press-award style). Unlike the trust badge (per-image, all/first), this one overlays the
        // WHOLE gallery frame: the desktop main image and the mobile carousel (persists across swipes). ───
        {
            id: 'overlayBadgeImage',
            type: 'image',
            label: 'Overlay badge',
            group: 'Overlay badge',
            info: 'Small badge image (e.g. press award) overlaid on the gallery. Empty = hidden.',
        },
        {
            id: 'overlayBadgePosition',
            type: 'select',
            label: 'Badge position',
            group: 'Overlay badge',
            default: 'top-left',
            options: [
                { value: 'top-left', label: 'Top left' },
                { value: 'top-right', label: 'Top right' },
                { value: 'bottom-left', label: 'Bottom left' },
                { value: 'bottom-right', label: 'Bottom right' },
            ],
        },
        {
            id: 'overlayBadgeSize',
            type: 'range',
            label: 'Badge size',
            group: 'Overlay badge',
            min: 6,
            max: 40,
            step: 2,
            default: 14,
            unit: '%',
        },
        // ─── ADDED: Padding (Shrine mobile/desktop padding header groups) ───
        {
            id: 'mobilePaddingTop',
            type: 'range',
            label: 'Mobile padding top',
            group: 'Padding',
            min: 0,
            max: 100,
            step: 4,
            default: 0,
            unit: 'px',
        },
        {
            id: 'mobilePaddingBottom',
            type: 'range',
            label: 'Mobile padding bottom',
            group: 'Padding',
            min: 0,
            max: 100,
            step: 4,
            default: 16,
            unit: 'px',
        },
        {
            id: 'desktopPaddingTop',
            type: 'range',
            label: 'Desktop padding top',
            group: 'Padding',
            min: 0,
            max: 100,
            step: 4,
            default: 36,
            unit: 'px',
        },
        {
            id: 'desktopPaddingBottom',
            type: 'range',
            label: 'Desktop padding bottom',
            group: 'Padding',
            min: 0,
            max: 100,
            step: 4,
            default: 36,
            unit: 'px',
        },
    ],
};
const RATIO = {
    natural: undefined,
    square: '1 / 1',
    portrait: '4 / 5',
    landscape: '3 / 2',
};
function Chevron({ dir }) {
    return (_jsx("svg", { width: "15", height: "15", viewBox: "0 0 16 16", fill: "none", style: dir === 'left' ? { transform: 'rotate(180deg)' } : undefined, "aria-hidden": "true", children: _jsx("path", { d: "M6 3l5 5-5 5", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }) }));
}
/**
 * The PHYSICAL side a logical control paints on.
 *
 * Every control here is positioned physically (`left`/`right`) or by a flex row, both of which already
 * flip with the reading direction — but the chevron GLYPH is drawn from a fixed path, so it does not.
 * On the Arabic shop the thumbnail strip's two carets ended up pointing at EACH OTHER: the "previous"
 * button had moved to the right edge of the row and was still drawing a left-pointing chevron. Deriving
 * the glyph and the placement from this one helper means they can no longer disagree.
 */
const sideOf = (logical, rtl) => logical === 'prev' ? (rtl ? 'right' : 'left') : rtl ? 'left' : 'right';
/**
 * Thumbnail strip with end caret buttons (Shopify/Shrine "slider-button" carets). When `show`, small
 * chevron buttons flank the scrollable list and step it ~80% of a view; each is DISABLED at its scroll
 * boundary (greyed) so a non-overflowing strip shows inert carets rather than dead ones. These are tiny
 * flat chevrons — NOT the overlay circles. SSR-safe: edge state is measured after mount. When `show` is
 * false the bare `<ul>` is returned (the strip scrolls natively).
 */
/**
 * Reading direction of a scroller, and the sign its horizontal deltas need.
 *
 * In a right-to-left scroller the spec starts `scrollLeft` at 0 on the RIGHT edge and runs it NEGATIVE as
 * the track advances. Every horizontal scroll below treated it as growing from the left, so on an Arabic
 * shop the product gallery's "next" went backward, its dot never left the first image, and the thumbnail
 * rail thought it was permanently at the start. This is the product page's main carousel — the most-seen
 * slider in the shop.
 */
function rtlOf(el) {
    return ((el.closest('[dir]')?.getAttribute('dir') ?? '').toLowerCase() === 'rtl' ||
        (typeof getComputedStyle === 'function' && getComputedStyle(el).direction === 'rtl'));
}
function ThumbStrip({ vertical, rtl, show, ulStyle, wrapStyle, labels, children, }) {
    const ref = useRef(null);
    const [edges, setEdges] = useState({ start: false, end: false });
    const measure = () => {
        const el = ref.current;
        if (!el)
            return;
        // `Math.abs` is the whole RTL fix here: horizontally, `scrollLeft` is ≤ 0 in a right-to-left rail, so
        // `pos > 1` was never true and the "scroll back" caret stayed hidden however far the rail had moved.
        const pos = vertical ? el.scrollTop : Math.abs(el.scrollLeft);
        const view = vertical ? el.clientHeight : el.clientWidth;
        const total = vertical ? el.scrollHeight : el.scrollWidth;
        setEdges({ start: pos > 1, end: pos + view < total - 1 });
    };
    useEffect(() => {
        measure();
        // measure once mounted; the scroll handler keeps it current thereafter.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    if (!show) {
        return (_jsx("ul", { ref: ref, style: { ...wrapStyle, ...ulStyle }, onScroll: measure, children: children }));
    }
    const step = (dir) => {
        const el = ref.current;
        if (!el)
            return;
        const sign = vertical || !rtlOf(el) ? 1 : -1;
        const amt = (vertical ? el.clientHeight : el.clientWidth) * 0.8 * dir * sign;
        el.scrollBy(vertical ? { top: amt, behavior: 'smooth' } : { left: amt, behavior: 'smooth' });
    };
    const caretStyle = (enabled) => ({
        flex: '0 0 auto',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        // Shrine `.thumbnail-slider .slider-button{width:2.5rem}` on a 44px-tall `.slider-button`.
        width: vertical ? '44px' : '25px',
        height: vertical ? '25px' : '44px',
        padding: 0,
        border: 'none',
        background: 'transparent',
        color: 'var(--aqx-scheme-text)',
        cursor: enabled ? 'pointer' : 'default',
        opacity: enabled ? 1 : 0.3,
    });
    const { order, ...ulRest } = ulStyle;
    return (_jsxs("div", { style: {
            display: 'flex',
            flexDirection: vertical ? 'column' : 'row',
            alignItems: 'center',
            gap: 0,
            minWidth: 0,
            ...(order != null ? { order } : null),
            ...wrapStyle,
        }, children: [_jsx("button", { type: "button", "aria-label": labels.previous, disabled: !edges.start, onClick: () => step(-1), style: caretStyle(edges.start), children: _jsx(Chevron, { dir: vertical ? 'left' : sideOf('prev', rtl) }) }), _jsx("ul", { ref: ref, style: { ...ulRest, flex: 1, minWidth: 0 }, onScroll: measure, children: children }), _jsx("button", { type: "button", "aria-label": labels.next, disabled: !edges.end, onClick: () => step(1), style: caretStyle(edges.end), children: _jsx(Chevron, { dir: vertical ? 'right' : sideOf('next', rtl) }) })] }));
}
/**
 * Minimal prev/next control overlaid on the image (Shrine-like, NOT a big white pill). Small,
 * semi-transparent, flat — subtle so it doesn't fight the image. Drives the MOBILE carousel arrows
 * and the opt-in DESKTOP 'sides' arrows (hidden by default — `desktop_arrows_position`). `transparent`
 * removes the fill (Shrine transparent_arrows); `bg` lets Shrine arrows_color_scheme drive the fill.
 */
const arrowStyle = (side, opts) => {
    // Shrine's `.slider-button`: `background:0 0` (transparent), no border, dark chevron at rgba(foreground,.9).
    // We reproduce that exactly: a BARE chevron, NO circle, NO white anywhere. A FILL appears ONLY if the seller
    // explicitly turns transparent OFF and picks an arrows colour scheme. The recurring "white div" was the old
    // opaque white circle (a fill on every default gallery) — there is no white element here at all now.
    const transparent = opts?.transparent === true;
    const fill = transparent ? undefined : opts?.bg;
    return {
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        ...(side === 'left' ? { left: '6px' } : { right: '6px' }),
        zIndex: 2,
        width: '44px', // Shrine slider-button size
        height: '44px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        background: fill ?? 'transparent',
        // Bare chevron in the scheme ink (Shrine foreground @ .9). A faint DARK shadow only — keeps it readable on
        // light images without ever painting a white shape behind it. Filled arrows take a CONTRAST-aware ink
        // from the caller — the old hardcoded '#fff' painted white-on-white with a light fill scheme.
        color: fill ? (opts?.ink ?? '#fff') : 'var(--aqx-scheme-text)',
        opacity: fill ? 1 : 0.9,
        boxShadow: 'none',
        filter: fill ? undefined : 'drop-shadow(0 1px 2px rgba(0,0,0,0.28))',
    };
};
/**
 * Lightbox prev/next control — white circle on the dark fullscreen backdrop (Shrine lightbox spec:
 * ~48px, rgba(255,255,255,.12) fill, white icon, ~16px from the left/right edge, vertically centered).
 * These white circles are CORRECT here (unlike on the light main image) because of the dark backdrop.
 */
const lightboxArrowStyle = (side) => ({
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    ...(side === 'left' ? { left: '16px' } : { right: '16px' }),
    width: '48px',
    height: '48px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    background: 'rgba(255,255,255,0.12)',
    color: '#fff',
});
/**
 * Shrine media gallery (self-contained — reads the product images from the shared product state).
 *
 * DESKTOP (page container ≥ 750px): a large main image + a thumbnail strip (below, or to the left).
 * Clicking a thumbnail swaps the main image — `active` client state. Hover-zoom is pure CSS.
 *
 * MOBILE (page container < 750px): the images become a horizontal scroll-snap CAROUSEL (swipeable),
 * the thumbnails hidden, position dots under it — Shrine's exact mobile gallery.
 *
 * Responsiveness mirrors `main-product`: an `@media(min-width:750px)` desktop rule (always holds on real
 * viewports + the builder's full-width canvas) plus an `@container aqx-page (max-width:749px)` override
 * placed LAST, so the builder's narrow device-preview reflows back to the mobile carousel.
 * SSR-safe: index 0 is the server-rendered default; hydration just attaches the click/scroll handlers.
 */
/**
 * The gallery with NO product media — the neutral scenic placeholder wearing the block's own presentation
 * settings. Same scoped-class + `<style>` shape as the real gallery (mobile-first padding, `@media` for
 * desktop, `@container` so the builder's device preview flips back to the mobile padding — see
 * responsive-container-query-trap), so switching between "no photo" and "photos" changes the images, never
 * the geometry the seller just configured.
 */
/**
 * A seller's CLIP inside the gallery (20/09/2026) — the same frame as a photo, and it STARTS ON ITS OWN.
 *
 * Muted autoplay in a loop, exactly like every other video surface of the theme (founder, 18/09/2026:
 * « always in components that have video, the play automatically should be on »). A clip that waits for a
 * press reads as a photograph, and the seller filmed it precisely so it would move.
 *
 * NO CHROME (founder, 20/09/2026, looking at her own product page: « ici on ne montre ni play ni stop
 * button ni le son, juste une vidéo en autoplay »). The browser's native bar — pause, 0:00 / 0:05, the
 * mute cross, fullscreen — was grey Chrome furniture sitting across the bottom of a styled gallery, in a
 * theme where every other clip plays bare. The clip is a moving photograph; it is framed like one.
 *
 * `poster` is what the frame paints before the first byte of video arrives, so the gallery never opens on
 * an empty box.
 */
function GalleryVideo({ item, productName, style, className, onClick, }) {
    // THE SHARED ENGINE, NOT A BARE `src` (21/09/2026). This used to be `<video src={item.url} autoPlay
    // preload="metadata">` — the seller's stored file, whole, for every visitor. Three things were wrong
    // with that at once, and the founder met all three on her own product page (« la vidéo est lente »):
    //   · no ladder — a phone downloaded the same file as a 4K screen, on an « Original » upload her own
    //     30 MB export;
    //   · TWO players per clip — the mobile carousel and the desktop frame are both in the DOM, one of
    //     them `display:none`, and a `<video autoplay src>` downloads whether or not it is painted, so
    //     every visitor pulled the clip twice, in parallel, on the same line;
    //   · nothing stepped down — a file that would not decode or would not arrive stayed an empty frame.
    // The engine markup (`preload="none"` + `<source data-src data-width>`) ships zero bytes; `initVideos`
    // promotes the rung that fits the painted box when the frame nears the viewport, plays it there, and
    // never touches the hidden layout (its box has no size, so its observer never fires). The poster is
    // what paints meanwhile — and it is preloaded from the page head when the clip opens the gallery.
    return (_jsx("video", { className: className, "data-aqx-gallery-video": "", ...videoContainerAttrs('autoplay'), "data-aqx-video-el": "", poster: item.posterUrl ?? undefined, muted: true, loop: true, playsInline: true, preload: "none", "aria-label": productImageAlt(item.alt, productName), style: onClick ? { ...style, cursor: 'zoom-in' } : style, onClick: onClick, children: videoSourceTags(clipSources(item)) }));
}
/**
 * A clip's THUMBNAIL: its poster when the asset service extracted one, else the clip itself with metadata
 * only (the browser then paints its first frame). Never a playing video — a thumbnail that downloads a
 * clip to show one frame is how a gallery of five videos becomes a 100 MB page.
 */
function VideoThumb({ item, productName, style, }) {
    return (_jsxs("span", { style: { position: 'relative', display: 'block', width: '100%', height: '100%' }, children: [item.posterUrl ? (_jsx(ResilientImg, { src: item.posterUrl, originalSrc: item.posterUrl, alt: productImageAlt(item.alt, productName), loading: "lazy", style: style })) : (_jsx("video", { "data-aqx-gallery-thumb": "", 
                // The SMALLEST rung: a thumbnail that reads a frame off the seller's own file reads it off
                // 30 MB of index and keyframe where the phone copy carries the same frame in a few kB.
                src: clipSources(item)[0]?.url ?? item.url, preload: "metadata", muted: true, playsInline: true, tabIndex: -1, style: style })), _jsx("span", { "aria-hidden": "true", style: {
                    position: 'absolute',
                    insetInlineStart: '50%',
                    top: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '22px',
                    height: '22px',
                    borderRadius: '999px',
                    background: 'rgba(0,0,0,0.55)',
                    color: '#fff',
                    display: 'grid',
                    placeItems: 'center',
                    pointerEvents: 'none',
                }, children: _jsx("svg", { viewBox: "0 0 24 24", width: "12", height: "12", fill: "currentColor", children: _jsx("path", { d: "M8 5.5v13l11-6.5z" }) }) })] }));
}
function EmptyGallery({ blockId, bs, scheme, }) {
    const cls = `aqx-mg-${blockId}`;
    const ratio = RATIO[bs.aspectRatio ?? 'square'];
    const radius = typeof bs.mobileMediaCornerRadius === 'number'
        ? `${num(bs.mobileMediaCornerRadius, 0)}px`
        : 'var(--aqx-theme-radius-media, 0px)';
    const mobilePad = `padding-top:${num(bs.mobilePaddingTop, 0)}px;padding-bottom:${num(bs.mobilePaddingBottom, 16)}px`;
    const css = `.${cls}{${mobilePad}}` +
        `@media(min-width:750px){.${cls}{padding-top:${num(bs.desktopPaddingTop, 36)}px;` +
        `padding-bottom:${num(bs.desktopPaddingBottom, 36)}px}}` +
        `@container aqx-page (max-width:749px){.${cls}{${mobilePad}}}`;
    // Both badges are the SELLER's own uploads, not product media, so they belong over the placeholder too.
    const trustBadge = bs.trustBadge;
    const overlayBadge = bs.overlayBadgeImage;
    const corner = (position, size) => ({
        position: 'absolute',
        zIndex: 3,
        width: `${size}%`,
        height: 'auto',
        pointerEvents: 'none',
        ...(position.startsWith('top') ? { top: '10px' } : { bottom: '10px' }),
        ...(position.endsWith('left') ? { left: '10px' } : { right: '10px' }),
    });
    return (_jsxs("div", { className: cls, "data-aqx-scheme": scheme, children: [_jsx("style", { dangerouslySetInnerHTML: { __html: css } }), _jsxs("div", { style: {
                    position: 'relative',
                    inlineSize: '100%',
                    aspectRatio: ratio ?? '1 / 1',
                    overflow: 'hidden',
                    borderRadius: radius,
                }, children: [_jsx(PlaceholderMedia, {}), typeof trustBadge?.url === 'string' && trustBadge.url ? (_jsx("img", { src: trustBadge.url, alt: "", "aria-hidden": "true", style: corner(str(bs.trustBadgePosition, 'top-right'), num(bs.badgeSizePercentage, 20)) })) : null, typeof overlayBadge?.url === 'string' && overlayBadge.url ? (_jsx("img", { src: overlayBadge.url, alt: "", "aria-hidden": "true", style: corner(str(bs.overlayBadgePosition, 'top-left'), num(bs.overlayBadgeSize, 14)) })) : null] })] }));
}
const MediaGalleryBlock = ({ block, context }) => {
    const ps = useProductState();
    // Builder canvas vs live storefront — drives thumbnail loading (see the `loading=` comment below).
    const edit = context.renderMode === 'edit';
    // Reading direction. The overlay arrows, the lightbox arrows and the thumbnail carets all take their
    // side AND their chevron from it, so they stay a matched pair on an Arabic shop.
    const rtl = context.dir === 'rtl';
    const prevSide = sideOf('prev', rtl);
    const nextSide = sideOf('next', rtl);
    // Resolved once here rather than inside `ThumbStrip`: that component takes no render context, and on
    // the storefront it is mounted by an island rather than by `PageRenderer`, so a `useT()` in there would
    // find no provider above it.
    const thumbLabels = {
        previous: t(context, 'a11y.previous_thumbnails'),
        next: t(context, 'a11y.next_thumbnails'),
    };
    const [active, setActive] = useState(0);
    const [slide, setSlide] = useState(0);
    // Fullscreen lightbox: null = closed, otherwise the index of the image shown over the dark backdrop.
    const [lightboxIndex, setLightboxIndex] = useState(null);
    // SSR guard — this section renders under react-dom/server AND hydrates as a client island; `document`
    // / `createPortal` are unavailable on the server, so the portal only renders once mounted in the browser.
    const [mounted, setMounted] = useState(false);
    const carouselRef = useRef(null);
    useEffect(() => setMounted(true), []);
    // Lightbox keyboard control — only bound while open: Esc closes, Arrow Left/Right navigate (wrapping).
    // Read the image count off the product here (the post-return `images` isn't in scope for the hook).
    // The photos of the SELECTED colour (see `imagesForVariant`): a product whose photos carry no option tag
    // — every product until the seller photographs an axis — gets the full list back, unchanged.
    // `ps.variantId` is normalised by the provider (see `effectiveVariantId`), so a strict lookup is safe
    // here and every product block reads the SAME variant.
    const currentLabel = ps?.variants.find((v) => v.id === ps.variantId)?.label ?? null;
    const shownImages = ps ? mediaForVariant(ps.product, currentLabel) : [];
    const lightboxCount = shownImages.length;
    // Switching colour must show that colour's FIRST photo, not photo #3 of the previous one — and with a
    // shorter set it would point past the end. Adjusted DURING render (React's sanctioned "derive state from
    // props" pattern) rather than in an effect: an effect would paint the stale photo for one frame, and on
    // the storefront that frame is the SSR HTML the buyer actually sees before hydration.
    const group = ps ? selectedImageGroup(ps.product, currentLabel) : null;
    const [shownGroup, setShownGroup] = useState(group);
    if (shownGroup !== group) {
        setShownGroup(group);
        setActive(0);
        setSlide(0);
        setLightboxIndex(null);
    }
    // Le `setSlide(0)` ci-dessus ne fait que remettre l'ÉTAT à la première photo : le carrousel, lui, est
    // un conteneur qui défile, et son `scrollLeft` reste là où le doigt l'avait laissé. Sans cette ligne,
    // changer de couleur depuis la photo 3 laissait l'acheteuse sur la photo 3 de la NOUVELLE couleur (ou
    // sur du vide quand celle-ci en a moins), pendant que les points disaient « 1 ». `behavior:'auto'` et
    // non `smooth` : on a demandé un changement INSTANTANÉ, une glissade de 300 ms n'en est pas un.
    useEffect(() => {
        const el = carouselRef.current;
        // `typeof` et non `el &&` : jsdom monte bien l'élément mais ne lui donne pas `scrollTo`, et un
        // rendu de test n'a pas à mourir sur une méthode de défilement que la machine ne peint pas.
        if (typeof el?.scrollTo === 'function')
            el.scrollTo({ left: 0, behavior: 'auto' });
    }, [group]);
    const lightboxOpen = lightboxIndex !== null;
    useEffect(() => {
        if (!lightboxOpen)
            return;
        const onKey = (e) => {
            if (e.key === 'Escape')
                setLightboxIndex(null);
            else if (e.key === 'ArrowLeft')
                setLightboxIndex((i) => (i === null ? i : (i - 1 + lightboxCount) % lightboxCount));
            else if (e.key === 'ArrowRight')
                setLightboxIndex((i) => (i === null ? i : (i + 1) % lightboxCount));
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [lightboxOpen, lightboxCount]);
    // Lock body scroll while the lightbox is open (guarded by `mounted` so `document` is never touched on SSR).
    useEffect(() => {
        if (!mounted || !lightboxOpen)
            return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev;
        };
    }, [mounted, lightboxOpen]);
    // THE VIDEO ENGINE, BOUND BY THE ISLAND (21/09/2026). On the storefront the SSR'd players are upgraded
    // once by the page's global `initVideos(document)`; those stay with that binding (see the one-binding
    // rule in `initVideo`). What the global init has never seen is every `<video>` React mounts LATER —
    // the desktop frame when the buyer clicks the clip's thumbnail, the colour swap that brings a tagged
    // clip in — and in the builder canvas there is no global init at all. So the block binds what is
    // unbound under its own root whenever the set of players can have changed, and unbinds its own on the
    // way out. The signature deliberately leaves the mobile `slide` out: swiping must not restart a clip.
    const rootRef = useRef(null);
    const engineSignature = `${shownImages.map((m) => m.url).join('|')}#${active}#${lightboxIndex ?? ''}`;
    useEffect(() => {
        const el = rootRef.current;
        if (!el)
            return undefined;
        return initVideos(el);
    }, [engineSignature]);
    // The lightbox lives in a portal on `<body>`, outside the root above — its player needs its own binding.
    const lightboxRef = useRef(null);
    useEffect(() => {
        const el = lightboxRef.current;
        if (!el)
            return undefined;
        return initVideos(el);
    }, [lightboxIndex]);
    if (!ps)
        return null;
    const images = shownImages;
    const bs = block.settings;
    // No product media yet (e.g. the demo product, which ships no images — the `main-product` fallback on
    // any non-product template) → the neutral scenic placeholder (variant B) instead of a blank column, so
    // the gallery stays visible + selectable.
    //
    // It renders through the block's OWN presentation settings — responsive paddings, aspect ratio, media
    // radius, colour scheme, and the two badge overlays (seller uploads, which exist with or without product
    // photos). This branch used to sit ABOVE `const bs = …` and paint a frozen 1:1 square: every single field
    // of this block's inspector was then a dead control, which is exactly how it read to the founder on the
    // home page (2026-09-13). A placeholder may say "no photo yet"; it must never say "your settings do
    // nothing".
    if (images.length === 0) {
        return (_jsx(EmptyGallery, { blockId: block.id, bs: bs, scheme: typeof bs.colorScheme === 'string' ? bs.colorScheme : undefined }));
    }
    // Shrine `media_fit` default is "contain" (un-cropped); only an explicit "cover" crops to fill.
    const fit = bs.mediaFit === 'cover' ? 'cover' : 'contain';
    const ratio = RATIO[bs.aspectRatio ?? 'square'];
    // Numeric width/height of `ratio` — used to keep the frame's shape when the viewport cap kicks in
    // (maxWidth = cap × factor). 'natural' has no fixed ratio → height cap only.
    const ratioFactor = { square: 1, portrait: 0.8, landscape: 1.5 }[bs.aspectRatio ?? 'square'];
    // Shrine mobile_media_corner_radius overrides the theme's image radius when the seller set it; untouched, the
    // gallery follows `--aqx-theme-radius-media` (0 = square by default, theme editor / section override).
    const radius = typeof bs.mobileMediaCornerRadius === 'number'
        ? `${num(bs.mobileMediaCornerRadius, 0)}px`
        : 'var(--aqx-theme-radius-media, 0px)';
    const thumbsLeft = bs.thumbnailPosition === 'left';
    const thumbsRight = bs.thumbnailPosition === 'right';
    const thumbsSide = thumbsLeft || thumbsRight;
    // ADDED — Shrine gallery_layout: 'stacked'/'columns' show every image at once (no active swap);
    // 'thumbnail'/'thumbnail_slider' keep the existing active-image + thumb-strip behaviour.
    const galleryLayout = str(bs.galleryLayout, 'thumbnail_slider');
    const stacked = galleryLayout === 'stacked' || galleryLayout === 'columns';
    const showThumbs = bs.showThumbnails !== false && !stacked && images.length > 1;
    // ADDED — Shrine desktop_thumbnails_count: how many thumbnails are visible before the strip scrolls.
    // For a SIDE strip it caps height; for a BOTTOM strip it caps the horizontal run. Beyond it the strip scrolls.
    const desktopThumbsCount = num(bs.desktopThumbnailsCount, 5);
    // Columns the bottom thumbnail strip splits into: at most `desktop_thumbnails_count`, but never more
    // than the image count (so 4 images fill the row in 4 slots; a longer set scrolls at N slots wide).
    const bottomThumbCols = Math.max(1, Math.min(desktopThumbsCount, images.length));
    // ADDED — Shrine desktop_vertical_thumbnails_width: width (% of gallery) of a left/right thumbnail column.
    const desktopVerticalThumbsWidth = num(bs.desktopVerticalThumbnailsWidth, 16);
    // ADDED — Shrine desktop_vertical_thumbnails_hide_arrows: hide the scroll arrows on a side thumbnail column.
    const desktopVerticalThumbsHideArrows = bs.desktopVerticalThumbnailsHideArrows === true;
    // ADDED — Shrine image_zoom_desktop: hover-zoom is on for 'hover'/'combined'; OUR legacy `zoom`
    // boolean still gates it (preserve default-on). Maps cleanly: zoom!==false && layout allows hover.
    const zoomMode = str(bs.imageZoomDesktop, 'combined');
    const zoom = bs.zoom !== false && zoomMode !== 'none' && zoomMode !== 'lightbox';
    // ADDED — Shrine hover_zoom_style: 'inline' magnifies in place (our existing scale-on-hover),
    // 'pane' uses a stronger magnification cursor cue. Only meaningful while hover-zoom is active.
    const hoverZoomPane = str(bs.hoverZoomStyle, 'inline') === 'pane';
    // Le survol peint l'image plus grande que sa boîte (`transform:scale`) — `sizes` doit le dire, sinon le
    // navigateur choisit un fichier trop petit et le zoom agrandit du vide. Le calcul vit dans le helper
    // partagé parce que le `<link rel="preload">` du `<head>` doit annoncer EXACTEMENT la même chaîne.
    const paintedSizes = galleryPaintedSizes(bs);
    // ADDED — Shrine image_zoom_mobile: 'lightbox' lets a tap open the image full-screen; 'none' disables it.
    const mobileZoomEnabled = str(bs.imageZoomMobile, 'lightbox') !== 'none';
    const multi = images.length > 1;
    // Shrine image_zoom_desktop default "combined" → clicking the main image opens the FULLSCREEN lightbox.
    // 'lightbox' and 'combined' both wire the click-to-open; 'hover'/'none' do not. Mobile mirrors image_zoom_mobile.
    const desktopLightbox = zoomMode === 'lightbox' || zoomMode === 'combined';
    const mobileLightbox = mobileZoomEnabled;
    const openLightbox = (i) => setLightboxIndex(i);
    // ADDED — Shrine arrows/dots color schemes + transparent arrows.
    // Default TRUE (bare chevrons, no circle) — `!== false` so an ABSENT field (every already-saved doc, which
    // predates this setting) also gets no circle. `=== true` here was the bug: it ignored the schema default and
    // left the old white-circle fill on every existing gallery. Sellers who want a filled pill set it to false.
    const transparentArrows = bs.transparentArrows !== false;
    const arrowScheme = str(bs.arrowsColorScheme, 'background-1');
    const arrowBg = transparentArrows ? undefined : schemeColor(arrowScheme);
    // Chevron ink CONTRASTS the fill: a light circle (background-1/2) gets the reference's soft-grey
    // glyph — never '#fff', which was invisible white-on-white; dark/accent fills keep a light glyph.
    const arrowInk = arrowScheme === 'background-1' || arrowScheme === 'background-2'
        ? 'color-mix(in srgb, var(--aqx-scheme-text) 70%, transparent)'
        : arrowScheme === 'text'
            ? 'var(--aqx-scheme-bg)'
            : 'var(--aqx-theme-accent-contrast, #fff)';
    const arrowOpts = { transparent: transparentArrows, bg: arrowBg, ink: arrowInk };
    const dotsColor = schemeColor(bs.dotsColorScheme ?? 'text');
    // The main image fills its column (Shrine); the column width is the section's media-size %. Tall images
    // are still capped by constrain_to_viewport below.
    const constrainToViewport = bs.constrainToViewport !== false;
    // ADDED — Shrine mobile_arrows_position / mobile_pagination.
    // Default 'hidden' — no overlay arrows on mobile (navigation is swipe + the thumbnail strip), which also
    // means no white-circle controls. The fallback applies to already-saved docs that never set the field.
    const mobileArrowsHidden = str(bs.mobileArrowsPosition, 'hidden') === 'hidden';
    const mobilePagination = str(bs.mobilePagination, 'dots_overlay');
    const mobileDots = mobilePagination === 'dots_under' || mobilePagination === 'dots_overlay';
    const mobileNumeric = mobilePagination === 'numeric';
    const mobileSlideGap = num(bs.mobileSpacingPixels, 0);
    // ADDED — Shrine mobile_thumbnails_position. Default 'bottom' — the thumbnail strip sits under the mobile
    // carousel so every image is visible on phones; 'hidden' = carousel + dots only. The fallback applies to
    // already-saved docs that never set the field.
    const mobileThumbsPosition = str(bs.mobileThumbnailsPosition, 'bottom');
    const mobileThumbsShown = mobileThumbsPosition !== 'hidden' && multi;
    const mobileThumbsSide = mobileThumbsPosition === 'left' || mobileThumbsPosition === 'right';
    // ADDED — Shrine mobile_thumbnails_count: how many thumbnails are visible before the strip scrolls
    // (mirrors desktop_thumbnails_count). A BOTTOM strip caps its width; a SIDE strip caps its height.
    const mobileThumbsCount = num(bs.mobileThumbnailsCount, 5);
    // Shrine `thumbnail-slider--no-slide` (media_count ≤ 3): a bottom strip of more than 3 images SLIDES —
    // prev/next carets appear and the strip bleeds through the section gutter like the image stage.
    const mobileThumbsSlide = !mobileThumbsSide && images.length > 3;
    // ADDED — Shrine thumbnails_ratio (square vs original thumbnail aspect).
    const thumbsSquare = str(bs.thumbnailsRatio, 'square') === 'square';
    // ADDED — Shrine trust_badge overlay.
    const trustBadge = bs.trustBadge;
    const trustBadgeUrl = typeof trustBadge?.url === 'string' ? trustBadge.url : '';
    const trustBadgePosition = str(bs.trustBadgePosition, 'top-right');
    const trustBadgeFirstOnly = str(bs.badgeDisplayOn, 'all') === 'first';
    const trustBadgeSize = num(bs.badgeSizePercentage, 20);
    // ADDED — desktop overlay arrows: the existing `desktopArrowsPosition` knob is now WIRED ('sides'
    // steps the active main image, wrapping). Default stays 'hidden' (Shrine) so every saved doc keeps its
    // current arrow-less desktop gallery; the arrows render as the same bare/filled chevrons as mobile.
    const desktopArrows = str(bs.desktopArrowsPosition, 'hidden') === 'sides' && multi && !stacked;
    // ADDED — slide counter chip ("current/total", top-right). Wired to the gallery's EXISTING slide
    // state: `active` on desktop, `slide` on mobile — SSR renders the initial "1/N", the client updates it
    // through the same state the thumbnails/carousel already drive. Multi-image only.
    const showCounter = bs.showCounter === true && multi;
    // ADDED — persistent overlay badge (press-award style): a small image overlaid on the gallery frame.
    const overlayBadge = bs.overlayBadgeImage;
    const overlayBadgeUrl = typeof overlayBadge?.url === 'string' ? overlayBadge.url : '';
    const overlayBadgePosition = str(bs.overlayBadgePosition, 'top-left');
    const overlayBadgeSize = num(bs.overlayBadgeSize, 14);
    // ADDED — Shrine color_scheme + responsive padding (mobile vs desktop).
    const colorScheme = bs.colorScheme;
    const mobilePadTop = num(bs.mobilePaddingTop, 0);
    const mobilePadBottom = num(bs.mobilePaddingBottom, 16);
    const desktopPadTop = num(bs.desktopPaddingTop, 36);
    const desktopPadBottom = num(bs.desktopPaddingBottom, 36);
    const trustBadgeStyle = () => {
        const v = trustBadgePosition;
        return {
            position: 'absolute',
            zIndex: 3,
            width: `${trustBadgeSize}%`,
            height: 'auto',
            pointerEvents: 'none',
            ...(v.startsWith('top') ? { top: '10px' } : { bottom: '10px' }),
            ...(v.endsWith('left') ? { left: '10px' } : { right: '10px' }),
        };
    };
    // The persistent overlay badge (press-award style) — same corner maths as the trust badge.
    const overlayBadgeStyle = () => {
        const v = overlayBadgePosition;
        return {
            position: 'absolute',
            zIndex: 3,
            width: `${overlayBadgeSize}%`,
            height: 'auto',
            pointerEvents: 'none',
            ...(v.startsWith('top') ? { top: '10px' } : { bottom: '10px' }),
            ...(v.endsWith('left') ? { left: '10px' } : { right: '10px' }),
        };
    };
    // Slide-counter chip ("current/total") — small dark rounded pill, top-right, over the image.
    const counterStyle = {
        position: 'absolute',
        top: '10px',
        right: '10px',
        zIndex: 3,
        padding: '3px 10px',
        borderRadius: '999px',
        background: 'rgba(0,0,0,0.55)',
        color: '#fff',
        fontSize: '12px',
        lineHeight: 1.5,
        fontFamily: 'var(--aqx-theme-font)',
        pointerEvents: 'none',
    };
    const activeImage = images[active] ?? images[0];
    if (!activeImage)
        return null;
    const cls = `aqx-mg-${block.id}`;
    const imgStyle = {
        width: '100%',
        height: '100%',
        objectFit: fit,
        display: 'block',
    };
    const frameBase = {
        position: 'relative',
        overflow: 'hidden',
        aspectRatio: ratio,
        borderRadius: radius,
        background: 'var(--aqx-scheme-surface)',
    };
    const onCarouselScroll = (e) => {
        const el = e.currentTarget;
        const idx = el.clientWidth > 0 ? Math.round(Math.abs(el.scrollLeft) / el.clientWidth) : 0;
        if (idx !== slide)
            setSlide(idx);
    };
    // Mobile: snap the carousel one slide left/right. Desktop: step the active main image (wrapping).
    const scrollCarousel = (dir) => {
        const el = carouselRef.current;
        if (el)
            el.scrollBy({ left: dir * el.clientWidth * (rtlOf(el) ? -1 : 1), behavior: 'smooth' });
    };
    // Mobile-first base = carousel; @media flips to desktop on real viewports; @container (LAST) flips back
    // to the carousel when the PAGE container is narrow (builder device-preview).
    // Desktop flex-direction: 'row' when thumbnails sit to the side (left/right), 'column' otherwise.
    // For thumbsRight the thumbnail strip uses CSS `order` to land after the main image.
    const css = `.${cls}{padding-top:${mobilePadTop}px;padding-bottom:${mobilePadBottom}px}` +
        `.${cls}__desktop{display:none}` +
        `.${cls}__mobile{display:block}` +
        `.${cls}__carousel{display:flex;overflow-x:auto;scroll-snap-type:x mandatory;gap:${mobileSlideGap}px;` +
        `-webkit-overflow-scrolling:touch;scrollbar-width:none}` +
        `.${cls}__carousel::-webkit-scrollbar{display:none}` +
        // `content-visibility:auto` on every slide but the FIRST is what stops the phone downloading the whole
        // gallery before a finger has moved. Chrome's lazy-loading distance threshold is generous — measured
        // 2026-09-15, a 412px viewport pulled FIVE slides of a scroll-snap track on load, and under PageSpeed's
        // throttling seven of them, ~1.2 MB of photos nobody had asked to see. A slide that is skipped for
        // rendering does not load its image at all (same mechanism as `display:none`, verified in Chrome), and
        // it loads normally the moment a swipe brings it near. The first slide is EXEMPT: it is the mobile LCP,
        // and containment has no upside on an element that paints immediately.
        // The `aspect-ratio` frame gives every slide its size without laying out its contents, so no
        // `contain-intrinsic-size` is needed and the track keeps its height — verified: skipped slides still
        // measure 396x396 in a 412px viewport.
        `.${cls}__slide{flex:0 0 100%;scroll-snap-align:center}` +
        `.${cls}__slide:not(:first-child){content-visibility:auto}` +
        // Shrine `.product__media-list` ≤749px: `margin-left:-2.5rem;width:calc(100% + 4rem)` — the mobile
        // carousel BLEEDS through the section's side gutter (universalSectionProps paddingInline =
        // `--aqx-theme-space-5`, 20px) so the image spans the full phone width, edge to edge (founder
        // reference 2026-09-11). Only the image stage bleeds: the thumbnail strip / dots below stay inside the
        // gutter. Skipped when the mobile thumbnails sit to the SIDE (the stage shares the row with them).
        // Base (mobile-first) rule only: the desktop @media hides `__mobile` entirely, and the @container
        // flip re-shows it with this base rule still in force — no reset needed.
        (mobileThumbsSide
            ? ''
            : `.${cls}__stage{margin-inline:calc(-1 * var(--aqx-theme-gutter,40px))}`) +
        `@media(min-width:750px){` +
        `.${cls}{padding-top:${desktopPadTop}px;padding-bottom:${desktopPadBottom}px}` +
        `.${cls}__desktop{display:flex;flex-direction:${thumbsSide ? 'row' : 'column'};gap:12px}` +
        // Shrine: the main image FILLS the media column (no width cap / centering) — width is constrained
        // upstream by the section's media-size % column. The thumbnail strip sits flush below.
        `.${cls}__main{width:100%}` +
        `.${cls}__mobile{display:none}` +
        `}` +
        `@container aqx-page (max-width:749px){` +
        // The desktop @media above keys off the browser WINDOW, which stays wide in the builder's device
        // preview — so the desktop padding leaks into the mobile preview and the Mobile-padding control looks
        // dead. Re-apply the MOBILE padding here (exactly like the display flip below) whenever the PAGE
        // container is narrow, so mobile padding wins on real phones AND in the builder. (See
        // responsive-container-query-trap: every mobile override ships under BOTH @media and @container.)
        `.${cls}{padding-top:${mobilePadTop}px;padding-bottom:${mobilePadBottom}px}` +
        `.${cls}__desktop{display:none}` +
        `.${cls}__mobile{display:block}` +
        `}` +
        (zoom
            ? `.${cls}__zoom img{transition:transform .45s ease}` +
                // Shrine hover_zoom_style: 'pane' magnifies harder than 'inline'.
                `.${cls}__zoom:hover img{transform:scale(${hoverZoomPane ? '1.6' : '1.08'})}`
            : '') +
        // `cursor:zoom-in` only where the lightbox is ACTUALLY wired (not merely hover-zoom) — the cue must
        // not mislead. The class is applied to the desktop main frame / mobile slides only when click-to-open is on.
        `.${cls}__cue{cursor:zoom-in}` +
        // Active thumbnail ring (Shrine: box-shadow 0 0 0 .1rem foreground) + hover dim (Shrine thumb hover opacity .7).
        `.${cls}__thumb{transition:opacity .15s ease,box-shadow .15s ease}` +
        `.${cls}__thumb:hover{opacity:.7}` +
        `.${cls}__thumb[aria-pressed="true"]{box-shadow:0 0 0 1px var(--aqx-scheme-text)}`;
    return (_jsxs("div", { className: cls, ref: rootRef, "data-aqx-scheme": typeof colorScheme === 'string' ? colorScheme : undefined, children: [_jsx("style", { dangerouslySetInnerHTML: { __html: css } }), _jsx(VariantImageWarmer, { product: ps.product, sizes: paintedSizes }), _jsxs("div", { className: `${cls}__mobile`, children: [_jsxs("div", { className: `${cls}__stage`, style: { position: 'relative' }, children: [_jsx("div", { className: `${cls}__carousel`, ref: carouselRef, onScroll: onCarouselScroll, children: images.map((img, i) => (_jsxs("div", { className: `${cls}__slide${zoom ? ` ${cls}__zoom` : ''}${mobileLightbox ? ` ${cls}__cue` : ''}`, style: frameBase, 
                                    // A clip opens the lightbox exactly like a photo: with no control bar of its own, a tap
                                    // on it means "bigger", the same thing it means on the photo beside it.
                                    role: mobileLightbox ? 'button' : undefined, "aria-label": mobileLightbox ? t(context, 'a11y.zoom_image') : undefined, onClick: mobileLightbox ? () => openLightbox(i) : undefined, children: [img.kind === 'video' ? (_jsx(GalleryVideo, { item: img, productName: ps.product.name, style: imgStyle })) : (_jsx(ResilientImg, { src: img.url, originalSrc: img.url, 
                                            // The stored 2560px original is 4x the pixels a phone can paint. `srcset` offers the
                                            // stored-variant ladder and lets the BROWSER pick by viewport and DPR; `src` stays the
                                            // original so a foreign image (no srcset) behaves exactly as it always did.
                                            srcSet: assetVariantSrcSet(img.url), 
                                            // Feature 0067: a photo of ours also comes in AVIF — a `<picture>` lets the browser
                                            // take it (~95 % of them) and keep the WebP `srcSet` above as the fallback.
                                            avifSrcSet: assetAvifSrcSet(img.url), sizes: paintedSizes, alt: productImageAlt(img.alt, ps.product.name), loading: i === 0 ? 'eager' : 'lazy', ...fetchPriorityAttr(i === 0 ? 'high' : undefined), style: imgStyle })), trustBadgeUrl && (!trustBadgeFirstOnly || i === 0) ? (_jsx("img", { src: trustBadgeUrl, alt: "", "aria-hidden": "true", style: trustBadgeStyle() })) : null] }, `slide-${i}`))) }), multi && !mobileArrowsHidden ? (_jsxs(_Fragment, { children: [_jsx("button", { type: "button", "aria-label": t(context, 'a11y.previous_image'), onClick: () => scrollCarousel(-1), style: arrowStyle(prevSide, arrowOpts), children: _jsx(Chevron, { dir: prevSide }) }), _jsx("button", { type: "button", "aria-label": t(context, 'a11y.next_image'), onClick: () => scrollCarousel(1), style: arrowStyle(nextSide, arrowOpts), children: _jsx(Chevron, { dir: nextSide }) })] })) : null, multi && mobileDots && mobilePagination === 'dots_overlay' ? (_jsx("div", { style: {
                                    position: 'absolute',
                                    left: 0,
                                    right: 0,
                                    bottom: '10px',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    gap: '6px',
                                    zIndex: 2,
                                }, children: images.map((img, i) => (_jsx("span", { style: {
                                        width: '7px',
                                        height: '7px',
                                        borderRadius: '50%',
                                        background: i === slide ? dotsColor : 'rgba(255,255,255,0.55)',
                                        transition: 'background .2s ease',
                                    } }, `odot-${img.url}-${i}`))) })) : null, showCounter ? (_jsx("span", { className: `${cls}__counter`, style: counterStyle, children: `${slide + 1}/${images.length}` })) : null, overlayBadgeUrl ? (_jsx("img", { src: overlayBadgeUrl, alt: "", "aria-hidden": "true", style: overlayBadgeStyle() })) : null] }), multi && mobileNumeric ? (_jsxs("div", { style: {
                            textAlign: 'center',
                            marginTop: '10px',
                            fontSize: '13px',
                            color: dotsColor,
                        }, children: [slide + 1, " / ", images.length] })) : null, multi && mobileDots && mobilePagination === 'dots_under' ? (_jsx("div", { style: { display: 'flex', justifyContent: 'center', gap: '6px', marginTop: '10px' }, children: images.map((img, i) => (_jsx("span", { style: {
                                width: '7px',
                                height: '7px',
                                borderRadius: '50%',
                                background: i === slide ? dotsColor : 'var(--aqx-scheme-border)',
                                transition: 'background .2s ease',
                            } }, `dot-${img.url}-${i}`))) })) : null, mobileThumbsShown ? (_jsx(ThumbStrip, { labels: thumbLabels, rtl: rtl, vertical: mobileThumbsSide, show: mobileThumbsSlide, wrapStyle: {
                            // Shrine `.product--thumbnail_slider .thumbnail-slider{padding-top:.5rem}`.
                            marginTop: '5px',
                            // Shrine: a SLIDING strip bleeds through the gutter (`margin-left/right:-1.5rem`), so the
                            // 2.5rem carets sit in the gutter and the thumbnails span the phone width (founder
                            // reference 2026-09-11). A 3-image strip (`--no-slide`) stays inside the gutter.
                            ...(mobileThumbsSlide
                                ? { marginInline: 'calc(-1 * var(--aqx-theme-gutter,40px))' }
                                : {}),
                        }, ulStyle: {
                            listStyle: 'none',
                            margin: 0,
                            // Shrine `.thumbnail-slider .thumbnail-list.slider{padding:.75rem .1rem;column-gap:.75rem}`;
                            // 3px inline keeps our 2px active ring un-clipped by the scroll container.
                            padding: mobileThumbsSide ? '3px' : '8px 3px',
                            // …but a SNAP container snaps the first thumb to its padding-box edge, scrolling those 3px
                            // (and the ring's left edge) out of view on load (founder screenshot 2026-09-13): the
                            // snapport must be inset by the same amount so `start` lands on the PADDED edge.
                            scrollPadding: mobileThumbsSide ? '3px' : '8px 3px',
                            display: 'flex',
                            flexDirection: mobileThumbsSide ? 'column' : 'row',
                            gap: '8px',
                            overflowX: mobileThumbsSide ? 'visible' : 'auto',
                            overflowY: mobileThumbsSide ? 'auto' : 'visible',
                            scrollbarWidth: 'none',
                            scrollSnapType: mobileThumbsSide ? undefined : 'x mandatory',
                            // left → before the carousel (order -1); bottom/right keep natural order.
                            order: mobileThumbsPosition === 'left' ? -1 : 0,
                            // A SIDE strip caps its height to N thumbs (52px + 8px gap), past which it scrolls; a BOTTOM
                            // strip sizes each thumb to 1/N of the width instead (see the <li>).
                            ...(mobileThumbsSide
                                ? { maxHeight: `${mobileThumbsCount * 52 + (mobileThumbsCount - 1) * 8}px` }
                                : {}),
                        }, children: images.map((img, i) => {
                            const selected = i === slide;
                            return (_jsx("li", { style: mobileThumbsSide
                                    ? undefined
                                    : {
                                        // Shrine `.thumbnail-list__item.slider__slide{width:calc((100% / var(--mobile-thumbnails)) - .5rem)}`
                                        // — N thumbs fill the row; with the .75rem gap the N-th peeks past the edge,
                                        // which is the cue that the strip scrolls.
                                        flex: `0 0 calc(100% / ${mobileThumbsCount} - 5px)`,
                                        scrollSnapAlign: 'start',
                                    }, children: _jsx("button", { type: "button", className: `${cls}__thumb`, "aria-label": t(context, 'a11y.go_to_image', { index: i + 1 }), "aria-pressed": selected, onClick: () => {
                                        const el = carouselRef.current;
                                        if (el) {
                                            el.scrollTo({
                                                left: i * el.clientWidth * (rtlOf(el) ? -1 : 1),
                                                behavior: 'smooth',
                                            });
                                        }
                                        setSlide(i);
                                    }, style: {
                                        padding: 0,
                                        cursor: 'pointer',
                                        // Bottom = 1/N of the row (the <li>); side = the narrow 52px column. Square via
                                        // aspect-ratio (Shrine `.thumbnail-list__item::before{padding-bottom:100%}`).
                                        width: mobileThumbsSide ? '52px' : '100%',
                                        aspectRatio: thumbsSquare ? '1' : undefined,
                                        height: 'auto',
                                        flexShrink: 0,
                                        overflow: 'hidden',
                                        // Square corners, always (founder 2026-09-11) — Shrine
                                        // `.thumbnail.global-media-settings img{border-radius:0}`.
                                        borderRadius: 0,
                                        // Constant hairline border; the ACTIVE state is the Shrine foreground RING
                                        // (box-shadow via the __thumb class) so there's no size shift between states.
                                        border: '1px solid var(--aqx-scheme-border)',
                                        background: 'var(--aqx-scheme-surface)',
                                    }, children: img.kind === 'video' ? (_jsx(VideoThumb, { item: img, productName: ps.product.name, style: {
                                            width: '100%',
                                            height: thumbsSquare ? '100%' : 'auto',
                                            objectFit: thumbsSquare ? 'cover' : 'contain',
                                            display: 'block',
                                        } })) : (_jsx(ResilientImg
                                    // A 160 px variant, not the slide's original: the box is ≤ 64 px, and the original
                                    // is what the (lazy) slide will fetch on its own when swiped to.
                                    , { 
                                        // A 160 px variant, not the slide's original: the box is ≤ 64 px, and the original
                                        // is what the (lazy) slide will fetch on its own when swiped to.
                                        src: assetVariantUrl(img.url, THUMBNAIL_VARIANT_WIDTH), 
                                        // Le repli si la VARIANTE rate : l'original, qui lui est toujours là.
                                        originalSrc: img.url, alt: productImageAlt(img.alt, ps.product.name), 
                                        // Thumbnails are EAGER in the BUILDER CANVAS only: there `loading="lazy"` raced
                                        // with the canvas layout and they sometimes never loaded — empty thumbnail boxes.
                                        // A live storefront has no such race, and eager thumbs actively hurt it: they
                                        // reuse the slides' URLs, so eager here overrides the carousel's own lazy slides
                                        // and pulls EVERY product image before the fold (~270 Ko instead of ~45 Ko on the
                                        // reference PDP). Lazy when live, eager when editing.
                                        loading: edit ? 'eager' : 'lazy', style: {
                                            width: '100%',
                                            height: thumbsSquare ? '100%' : 'auto',
                                            objectFit: thumbsSquare ? 'cover' : 'contain',
                                            display: 'block',
                                        } })) }) }, `mthumb-${i}`));
                        }) })) : null] }), _jsxs("div", { className: `${cls}__desktop`, children: [showThumbs ? (_jsx(ThumbStrip, { labels: thumbLabels, rtl: rtl, vertical: thumbsSide, show: !thumbsSide && images.length > 4, ulStyle: {
                            listStyle: 'none',
                            margin: 0,
                            // 3px breathing room: the strip is a scroll container, so the ACTIVE thumb's 2px
                            // box-shadow ring would be CLIPPED on the edge thumbnails with padding 0.
                            padding: '3px',
                            display: 'flex',
                            flexDirection: thumbsSide ? 'column' : 'row',
                            flexWrap: thumbsSide ? 'nowrap' : 'wrap',
                            gap: '8px',
                            // LEFT strip sits BEFORE the main image (order 0); BOTTOM (column) + RIGHT sit AFTER it
                            // (order 2) so bottom thumbnails render UNDER the main image, not above it.
                            order: thumbsLeft ? 0 : 2,
                            // ADDED — Shrine desktop_vertical_thumbnails_width: a side strip occupies a % of the gallery.
                            ...(thumbsSide
                                ? {
                                    flex: `0 0 ${desktopVerticalThumbsWidth}%`,
                                    // ADDED — Shrine desktop_thumbnails_count: side strip scrolls past N thumbs (64px + 8px gap).
                                    maxHeight: `${desktopThumbsCount * 64 + (desktopThumbsCount - 1) * 8}px`,
                                    overflowY: 'auto',
                                    // Shrine desktop_vertical_thumbnails_hide_arrows: this strip scrolls natively (no custom
                                    // arrows); hiding native scrollbars when arrows are hidden keeps the strip clean.
                                    ...(desktopVerticalThumbsHideArrows
                                        ? { scrollbarWidth: 'none' }
                                        : null),
                                }
                                : {
                                    // Shrine: the bottom strip spans the FULL column width; each thumb is a fraction of it
                                    // (see the <li> flex) so they fill the row aligned under the main image, and a long set
                                    // scrolls horizontally past `desktop_thumbnails_count`.
                                    width: '100%',
                                    overflowX: 'auto',
                                    flexWrap: 'nowrap',
                                }),
                        }, children: images.map((img, i) => {
                            const selected = i === active;
                            return (_jsx("li", { style: thumbsSide
                                    ? { listStyle: 'none' }
                                    : {
                                        listStyle: 'none',
                                        // Each bottom thumb fills 1/N of the row (N = min(count, desktop_thumbnails_count)),
                                        // so a few images span the full width and a longer set scrolls.
                                        flex: `0 0 calc((100% - ${(bottomThumbCols - 1) * 8}px) / ${bottomThumbCols})`,
                                    }, children: _jsx("button", { type: "button", className: `${cls}__thumb`, "aria-label": t(context, 'a11y.go_to_image', { index: i + 1 }), "aria-pressed": selected, onClick: () => setActive(i), style: {
                                        padding: 0,
                                        cursor: 'pointer',
                                        // Fill the <li> width (bottom = proportional, side = the narrow strip's 64px); square
                                        // via aspect-ratio so height tracks the responsive width.
                                        width: thumbsSide ? '64px' : '100%',
                                        aspectRatio: thumbsSquare ? '1' : undefined,
                                        height: thumbsSquare ? undefined : 'auto',
                                        flexShrink: 0,
                                        overflow: 'hidden',
                                        // Square corners, always (founder 2026-09-11) — Shrine
                                        // `.thumbnail.global-media-settings img{border-radius:0}`.
                                        borderRadius: 0,
                                        // Constant hairline border; ACTIVE = Shrine foreground RING (box-shadow via __thumb),
                                        // hover dims to .7. No size shift between active/inactive.
                                        border: '1px solid var(--aqx-scheme-border)',
                                        background: 'var(--aqx-scheme-surface)',
                                    }, children: img.kind === 'video' ? (_jsx(VideoThumb, { item: img, productName: ps.product.name, style: {
                                            width: '100%',
                                            height: thumbsSquare ? '100%' : 'auto',
                                            objectFit: thumbsSquare ? 'cover' : 'contain',
                                            display: 'block',
                                        } })) : (_jsx(ResilientImg
                                    // A 160 px variant, not the slide's original: the box is ≤ 64 px, and the original
                                    // is what the (lazy) slide will fetch on its own when swiped to.
                                    , { 
                                        // A 160 px variant, not the slide's original: the box is ≤ 64 px, and the original
                                        // is what the (lazy) slide will fetch on its own when swiped to.
                                        src: assetVariantUrl(img.url, THUMBNAIL_VARIANT_WIDTH), 
                                        // Le repli si la VARIANTE rate : l'original, qui lui est toujours là.
                                        originalSrc: img.url, alt: productImageAlt(img.alt, ps.product.name), 
                                        // Thumbnails are EAGER in the BUILDER CANVAS only: there `loading="lazy"` raced
                                        // with the canvas layout and they sometimes never loaded — empty thumbnail boxes.
                                        // A live storefront has no such race, and eager thumbs actively hurt it: they
                                        // reuse the slides' URLs, so eager here overrides the carousel's own lazy slides
                                        // and pulls EVERY product image before the fold (~270 Ko instead of ~45 Ko on the
                                        // reference PDP). Lazy when live, eager when editing.
                                        loading: edit ? 'eager' : 'lazy', style: {
                                            width: '100%',
                                            height: thumbsSquare ? '100%' : 'auto',
                                            objectFit: thumbsSquare ? 'cover' : 'contain',
                                            display: 'block',
                                        } })) }) }, `thumb-${i}`));
                        }) })) : null, _jsx("div", { className: `${cls}__main`, style: {
                            flex: '1 1 auto',
                            minWidth: 0,
                            order: 1,
                        }, children: _jsxs("div", { className: `${zoom ? `${cls}__zoom` : ''}${desktopLightbox ? ` ${cls}__cue` : ''}`.trim() ||
                                undefined, 
                            // Viewport cap ON THE FRAME, not the wrapper: an aspect-ratio box ignores its parent's
                            // max-height and would overflow it, painting over the thumbnails and the sections below
                            // (the "full-width section = giant image hides the page" bug). Capping height AND the
                            // ratio-matched width keeps the frame's shape and centres it in the column.
                            style: {
                                ...frameBase,
                                ...(constrainToViewport
                                    ? {
                                        maxHeight: '85vh',
                                        ...(ratioFactor
                                            ? { maxWidth: `calc(85vh * ${ratioFactor})`, marginInline: 'auto' }
                                            : null),
                                    }
                                    : null),
                            }, children: [activeImage.kind === 'video' ? (_jsx(GalleryVideo, { item: activeImage, productName: ps.product.name, style: imgStyle, onClick: desktopLightbox ? () => openLightbox(active) : undefined })) : (_jsx(ResilientImg, { src: activeImage.url, originalSrc: activeImage.url, 
                                    // See the mobile slide above: the ladder, not the 2560px original, is what a page load pulls.
                                    srcSet: assetVariantSrcSet(activeImage.url), avifSrcSet: assetAvifSrcSet(activeImage.url), sizes: paintedSizes, alt: productImageAlt(activeImage.alt, ps.product.name), loading: "eager", ...fetchPriorityAttr('high'), style: imgStyle, role: desktopLightbox ? 'button' : undefined, "aria-label": desktopLightbox ? t(context, 'a11y.zoom_image') : undefined, onClick: desktopLightbox ? () => openLightbox(active) : undefined })), trustBadgeUrl ? (_jsx("img", { src: trustBadgeUrl, alt: "", "aria-hidden": "true", style: trustBadgeStyle() })) : null, showCounter ? (_jsx("span", { className: `${cls}__counter`, style: counterStyle, children: `${active + 1}/${images.length}` })) : null, overlayBadgeUrl ? (_jsx("img", { src: overlayBadgeUrl, alt: "", "aria-hidden": "true", style: overlayBadgeStyle() })) : null, desktopArrows ? (_jsxs(_Fragment, { children: [_jsx("button", { type: "button", "aria-label": t(context, 'a11y.previous_image'), onClick: () => setActive((a) => (a - 1 + images.length) % images.length), style: arrowStyle(prevSide, arrowOpts), children: _jsx(Chevron, { dir: prevSide }) }), _jsx("button", { type: "button", "aria-label": t(context, 'a11y.next_image'), onClick: () => setActive((a) => (a + 1) % images.length), style: arrowStyle(nextSide, arrowOpts), children: _jsx(Chevron, { dir: nextSide }) })] })) : null] }) })] }), mounted && lightboxIndex !== null && images[lightboxIndex]
                ? createPortal(_jsxs("div", { role: "dialog", "aria-modal": "true", "aria-label": t(context, 'a11y.image_gallery'), onClick: () => setLightboxIndex(null), style: {
                        position: 'fixed',
                        inset: 0,
                        zIndex: 9999,
                        background: 'rgba(0,0,0,0.92)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }, children: [images[lightboxIndex].kind === 'video' ? (
                        // Reachable through the arrows/dots: the buyer zoomed a photo, then stepped onto a clip.
                        // It plays here rather than painting the broken-picture glyph an `<img src=…mp4>` would.
                        _jsx("div", { ref: lightboxRef, onClick: (e) => e.stopPropagation(), children: _jsx(GalleryVideo, { item: images[lightboxIndex], productName: ps.product.name, style: {
                                    maxWidth: 'min(92vw, 1400px)',
                                    maxHeight: '90vh',
                                    objectFit: 'contain',
                                    display: 'block',
                                } }) })) : (_jsx(ResilientImg, { src: images[lightboxIndex].url, alt: productImageAlt(images[lightboxIndex].alt, ps.product.name), onClick: (e) => e.stopPropagation(), style: {
                                maxWidth: 'min(92vw, 1400px)',
                                maxHeight: '90vh',
                                objectFit: 'contain',
                                display: 'block',
                            } })), _jsx("button", { type: "button", "aria-label": t(context, 'a11y.close'), onClick: (e) => {
                                e.stopPropagation();
                                setLightboxIndex(null);
                            }, style: {
                                position: 'absolute',
                                top: '16px',
                                right: '16px',
                                width: '44px',
                                height: '44px',
                                borderRadius: '50%',
                                border: 'none',
                                padding: 0,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: 'rgba(255,255,255,0.12)',
                                color: '#fff',
                            }, children: _jsx("svg", { width: "18", height: "18", viewBox: "0 0 16 16", fill: "none", "aria-hidden": "true", children: _jsx("path", { d: "M4 4l8 8M12 4l-8 8", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round" }) }) }), multi ? (_jsxs(_Fragment, { children: [_jsx("button", { type: "button", "aria-label": t(context, 'a11y.previous_image'), onClick: (e) => {
                                        e.stopPropagation();
                                        setLightboxIndex((i) => i === null ? i : (i - 1 + images.length) % images.length);
                                    }, style: lightboxArrowStyle(prevSide), children: _jsx(Chevron, { dir: prevSide }) }), _jsx("button", { type: "button", "aria-label": t(context, 'a11y.next_image'), onClick: (e) => {
                                        e.stopPropagation();
                                        setLightboxIndex((i) => (i === null ? i : (i + 1) % images.length));
                                    }, style: lightboxArrowStyle(nextSide), children: _jsx(Chevron, { dir: nextSide }) }), _jsx("div", { onClick: (e) => e.stopPropagation(), style: {
                                        position: 'absolute',
                                        left: 0,
                                        right: 0,
                                        bottom: '20px',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        gap: '8px',
                                    }, children: images.map((img, i) => (_jsx("button", { type: "button", "aria-label": t(context, 'a11y.go_to_image', { index: i + 1 }), "aria-pressed": i === lightboxIndex, onClick: (e) => {
                                            e.stopPropagation();
                                            setLightboxIndex(i);
                                        }, style: {
                                            width: '8px',
                                            height: '8px',
                                            padding: 0,
                                            border: 'none',
                                            borderRadius: '50%',
                                            cursor: 'pointer',
                                            background: i === lightboxIndex ? '#fff' : 'rgba(255,255,255,0.3)',
                                        } }, `lbdot-${img.url}-${i}`))) })] })) : null] }), document.body)
                : null] }));
};
export const mediaGalleryBlock = {
    type: 'media-gallery',
    schema: mediaGalleryBlockSchema,
    Component: MediaGalleryBlock,
    meta: { label: 'Media gallery' },
};
//# sourceMappingURL=index.js.map